import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Chip, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress, Alert, IconButton, Tooltip,
  Divider, FormGroup, FormControlLabel, Checkbox, Accordion,
  AccordionSummary, AccordionDetails, InputAdornment,
} from '@mui/material';
import {
  PersonAdd, Refresh, Edit, Delete, Key,
  ExpandMore, Visibility, VisibilityOff, Badge,
} from '@mui/icons-material';
import DataTable, { Column } from '../../../components/Common/DataTable';
import api from '../../../services/api';

// ─── tipos ───────────────────────────────────────────────────────────────────

interface Colaborador {
  id: number;
  username: string;
  email: string;
  nome: string;
  estado: string;
  dataRegisto: string;
  ultimoLogin: string | null;
  totalPermissoes: number;
}

interface Permissao {
  id: string;
  codigo: string;
  nome: string;
  descricao: string;
  modulo: string;
  accao: string;
}

// ─── permissões agrupadas por módulo ─────────────────────────────────────────

const MODULE_LABELS: Record<string, string> = {
  DASHBOARD: 'Dashboard',
  PAGAMENTOS: 'Pagamentos',
  FATURACAO: 'Faturação',
  RELATORIOS: 'Relatórios',
  ENTIDADES: 'Entidades',
  UTILIZADORES: 'Utilizadores',
  CLIENTES: 'Clientes',
  SERVICOS: 'Serviços',
  SISP: 'SISP',
  SEGURANCA: 'Segurança & Perfis',
  EVENTOS: 'Eventos',
  DOACOES: 'Doações',
  SHOP: 'Pagali Shop',
  NOTIFICACOES: 'Notificações',
  BANKING: 'Banking',
  AGENTES: 'Agentes',
  CMS: 'CMS',
  CONFIG: 'Configurações',
  HUB: 'Hub Pagali',
  PUBLICIDADE: 'Publicidade',
  MARKETING: 'Marketing',
  COMUNICACAO: 'Comunicação',
  COLABORADORES: 'Colaboradores',
  FOOD: 'Food / iMenu',
  SISTEMA: 'Sistema',
};

// ─── componente principal ────────────────────────────────────────────────────

const Colaboradores: React.FC = () => {
  const [colaboradores, setColaboradores] = useState<Colaborador[]>([]);
  const [permissoesDisponiveis, setPermissoesDisponiveis] = useState<Permissao[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // diálogo criar / editar
  const [dlg, setDlg] = useState(false);
  const [dlgMode, setDlgMode] = useState<'create' | 'editPerms'>('create');
  const [dlgTarget, setDlgTarget] = useState<Colaborador | null>(null);
  const [dlgLoading, setDlgLoading] = useState(false);
  const [dlgError, setDlgError] = useState<string | null>(null);

  // form criar
  const [formNome, setFormNome] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [existingUser, setExistingUser] = useState<{ found: boolean; nome?: string } | null>(null);
  const [checkingEmail, setCheckingEmail] = useState(false);

  // permissões seleccionadas
  const [selectedPerms, setSelectedPerms] = useState<Set<string>>(new Set());

  // confirmar desactivar
  const [deleteDlg, setDeleteDlg] = useState<Colaborador | null>(null);

  // ── carregar dados ──────────────────────────────────────────────────────────

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [colRes, permRes] = await Promise.all([
        api.get('/admin/colaboradores'),
        api.get('/admin/permissions?limit=200'),
      ]);
      setColaboradores(colRes.data?.data || []);
      setPermissoesDisponiveis(permRes.data?.data || []);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── helpers ─────────────────────────────────────────────────────────────────

  const permsByModule = permissoesDisponiveis.reduce((acc, p) => {
    if (!acc[p.modulo]) acc[p.modulo] = [];
    acc[p.modulo].push(p);
    return acc;
  }, {} as Record<string, Permissao[]>);

  const togglePerm = (codigo: string) => {
    setSelectedPerms(prev => {
      const next = new Set(prev);
      next.has(codigo) ? next.delete(codigo) : next.add(codigo);
      return next;
    });
  };

  const toggleModule = (modulo: string) => {
    const modPerms = permsByModule[modulo]?.map(p => p.codigo) || [];
    const allSelected = modPerms.every(c => selectedPerms.has(c));
    setSelectedPerms(prev => {
      const next = new Set(prev);
      if (allSelected) modPerms.forEach(c => next.delete(c));
      else modPerms.forEach(c => next.add(c));
      return next;
    });
  };

  // ── abrir diálogo criar ─────────────────────────────────────────────────────

  const openCreate = () => {
    setDlgMode('create');
    setDlgTarget(null);
    setFormNome(''); setFormEmail(''); setFormPassword('');
    setSelectedPerms(new Set());
    setDlgError(null);
    setExistingUser(null);
    setDlg(true);
  };

  const checkEmail = async (email: string) => {
    if (!email.includes('@')) { setExistingUser(null); return; }
    setCheckingEmail(true);
    try {
      // GET /admin/users?search=email para ver se já existe
      const r = await api.get(`/admin/users?search=${encodeURIComponent(email)}&limit=1`);
      const users = r.data?.data || [];
      const match = users.find((u: any) => u.email?.toLowerCase() === email.toLowerCase());
      if (match) {
        setExistingUser({ found: true, nome: match.name || match.username || match.email });
        if (!formNome) setFormNome(match.name || '');
      } else {
        setExistingUser({ found: false });
      }
    } catch { setExistingUser(null); }
    finally { setCheckingEmail(false); }
  };

  // ── abrir diálogo editar permissões ─────────────────────────────────────────

  const openEditPerms = async (c: Colaborador) => {
    setDlgMode('editPerms');
    setDlgTarget(c);
    setDlgError(null);
    setSelectedPerms(new Set());
    setDlg(true);
    try {
      const r = await api.get(`/admin/colaboradores/${c.id}`);
      const perms: string[] = r.data?.data?.permissoes || [];
      setSelectedPerms(new Set(perms));
    } catch { /* keep empty */ }
  };

  // ── submeter criar ──────────────────────────────────────────────────────────

  const handleCreate = async () => {
    if (!formEmail.trim()) { setDlgError('Email é obrigatório'); return; }
    if (!existingUser?.found && (!formNome.trim() || !formPassword.trim())) {
      setDlgError('Para novo colaborador: nome e password são obrigatórios');
      return;
    }
    setDlgLoading(true);
    setDlgError(null);
    try {
      await api.post('/admin/colaboradores', {
        nome: formNome,
        email: formEmail,
        password: formPassword,
        permissoes: Array.from(selectedPerms),
      });
      setDlg(false);
      loadAll();
    } catch (e: any) {
      setDlgError(e.response?.data?.error || 'Erro ao criar colaborador');
    } finally {
      setDlgLoading(false);
    }
  };

  // ── submeter permissões ─────────────────────────────────────────────────────

  const handleSavePerms = async () => {
    if (!dlgTarget) return;
    setDlgLoading(true);
    setDlgError(null);
    try {
      await api.put(`/admin/colaboradores/${dlgTarget.id}/permissions`, {
        permissoes: Array.from(selectedPerms),
      });
      setDlg(false);
      loadAll();
    } catch (e: any) {
      setDlgError(e.response?.data?.error || 'Erro ao guardar permissões');
    } finally {
      setDlgLoading(false);
    }
  };

  // ── desativar ───────────────────────────────────────────────────────────────

  const handleDelete = async (c: Colaborador) => {
    try {
      await api.delete(`/admin/colaboradores/${c.id}`);
      setDeleteDlg(null);
      loadAll();
    } catch { /* silent */ }
  };

  // ── tabela ──────────────────────────────────────────────────────────────────

  const columns: Column<Colaborador>[] = [
    {
      id: 'nome',
      label: 'Colaborador',
      format: (_val, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: 14 }}>
            {(row.nome || row.email)[0]?.toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={600}>{row.nome || '—'}</Typography>
            <Typography variant="caption" color="text.secondary">{row.email}</Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'totalPermissoes',
      label: 'Permissões',
      format: (_val, row) => (
        <Chip
          icon={<Key sx={{ fontSize: 14 }} />}
          label={`${row.totalPermissoes} permissões`}
          size="small"
          color={row.totalPermissoes > 0 ? 'primary' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      id: 'estado',
      label: 'Estado',
      format: (_val, row) => (
        <Chip
          label={row.estado === 'A' ? 'Ativo' : 'Inativo'}
          size="small"
          color={row.estado === 'A' ? 'success' : 'default'}
        />
      ),
    },
    {
      id: 'ultimoLogin',
      label: 'Último Login',
      format: (_val, row) => row.ultimoLogin
        ? new Date(row.ultimoLogin).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })
        : '—',
    },
    {
      id: 'dataRegisto',
      label: 'Criado',
      format: (_val, row) => new Date(row.dataRegisto).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }),
    },
    {
      id: 'id',
      label: 'Ações',
      format: (_val, row) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Editar permissões">
            <IconButton size="small" onClick={() => openEditPerms(row)}>
              <Key fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={row.estado === 'A' ? 'Desativar' : 'Ativar'}>
            <IconButton
              size="small"
              color={row.estado === 'A' ? 'error' : 'success'}
              onClick={() => setDeleteDlg(row)}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // ── render ──────────────────────────────────────────────────────────────────

  const PermissionsPanel = () => (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Seleciona as permissões que este colaborador terá no painel de administração.
      </Typography>
      {Object.entries(permsByModule).map(([modulo, perms]) => {
        const allSel = perms.every(p => selectedPerms.has(p.codigo));
        const someSel = perms.some(p => selectedPerms.has(p.codigo));
        return (
          <Accordion key={modulo} disableGutters sx={{ mb: 0.5, border: '1px solid', borderColor: 'divider' }} elevation={0}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                <Checkbox
                  size="small"
                  checked={allSel}
                  indeterminate={someSel && !allSel}
                  onClick={(e) => { e.stopPropagation(); toggleModule(modulo); }}
                />
                <Typography variant="body2" fontWeight={600}>
                  {MODULE_LABELS[modulo] || modulo}
                </Typography>
                {someSel && (
                  <Chip label={`${perms.filter(p => selectedPerms.has(p.codigo)).length}/${perms.length}`} size="small" color="primary" sx={{ ml: 'auto', mr: 1 }} />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <FormGroup>
                {perms.map(p => (
                  <FormControlLabel
                    key={p.codigo}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedPerms.has(p.codigo)}
                        onChange={() => togglePerm(p.codigo)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">{p.nome}</Typography>
                        {p.descricao && (
                          <Typography variant="caption" color="text.secondary">{p.descricao}</Typography>
                        )}
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', mb: 0.5 }}
                  />
                ))}
              </FormGroup>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Badge color="primary" /> Colaboradores
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Funcionários Pagali com acesso ao painel de administração
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<Refresh />} variant="outlined" onClick={loadAll} disabled={loading}>
            Atualizar
          </Button>
          <Button startIcon={<PersonAdd />} variant="contained" onClick={openCreate}>
            Adicionar Colaborador
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        {[
          { label: 'Total', value: colaboradores.length, color: 'primary.main' },
          { label: 'Ativos', value: colaboradores.filter(c => c.estado === 'A').length, color: 'success.main' },
          { label: 'Inativos', value: colaboradores.filter(c => c.estado !== 'A').length, color: 'text.secondary' },
        ].map(s => (
          <Card key={s.label} sx={{ minWidth: 120 }} variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h4" fontWeight={700} color={s.color}>{s.value}</Typography>
              <Typography variant="caption" color="text.secondary">{s.label}</Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Tabela */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataTable columns={columns} data={colaboradores} />
      )}

      {/* ─── Diálogo criar / editar permissões ─── */}
      <Dialog open={dlg} onClose={() => !dlgLoading && setDlg(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {dlgMode === 'create' ? 'Novo Colaborador' : `Permissões — ${dlgTarget?.nome || dlgTarget?.email}`}
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {dlgError && <Alert severity="error" sx={{ mb: 2 }}>{dlgError}</Alert>}

          {dlgMode === 'create' && (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                <TextField
                  label="Email"
                  type="email"
                  value={formEmail}
                  onChange={e => { setFormEmail(e.target.value); setExistingUser(null); }}
                  onBlur={e => checkEmail(e.target.value)}
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: checkingEmail
                      ? <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment>
                      : undefined,
                  }}
                />
                {existingUser?.found && (
                  <Alert severity="info">
                    Utilizador Pagali encontrado: <strong>{existingUser.nome}</strong>. A conta existente será associada como Colaborador — sem criar nova conta nem alterar password.
                  </Alert>
                )}
                {existingUser?.found === false && (
                  <Alert severity="success">Email disponível — será criada uma nova conta.</Alert>
                )}
                {!existingUser?.found && (
                  <>
                    <TextField
                      label="Nome completo"
                      value={formNome}
                      onChange={e => setFormNome(e.target.value)}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      value={formPassword}
                      onChange={e => setFormPassword(e.target.value)}
                      fullWidth
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setShowPassword(v => !v)}>
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </>
                )}
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Permissões
              </Typography>
            </>
          )}

          <PermissionsPanel />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg(false)} disabled={dlgLoading}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={dlgMode === 'create' ? handleCreate : handleSavePerms}
            disabled={dlgLoading}
            startIcon={dlgLoading ? <CircularProgress size={16} /> : <Edit />}
          >
            {dlgMode === 'create' ? 'Criar Colaborador' : 'Guardar Permissões'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Confirmar desactivar ─── */}
      <Dialog open={!!deleteDlg} onClose={() => setDeleteDlg(null)} maxWidth="xs">
        <DialogTitle>
          {deleteDlg?.estado === 'A' ? 'Desativar Colaborador' : 'Ativar Colaborador'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {deleteDlg?.estado === 'A'
              ? `Tens a certeza que queres desativar ${deleteDlg?.nome || deleteDlg?.email}? O acesso ao painel será revogado imediatamente.`
              : `Reativar ${deleteDlg?.nome || deleteDlg?.email}?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDlg(null)}>Cancelar</Button>
          <Button
            variant="contained"
            color={deleteDlg?.estado === 'A' ? 'error' : 'success'}
            onClick={() => deleteDlg && handleDelete(deleteDlg)}
          >
            {deleteDlg?.estado === 'A' ? 'Desativar' : 'Ativar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Colaboradores;
